import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError, mediaUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Download, ExternalLink, FileText, MessageCircle, Phone, RefreshCw } from "lucide-react";
import RxConvert from "@/components/admin/RxConvert";
import RxPayLink from "@/components/admin/RxPayLink";

const STATUS_TONE = {
  new: "border-secondary/50 text-secondary bg-secondary/10",
  called: "border-sky-500/40 text-sky-700 bg-sky-50",
  confirmed: "border-primary/40 text-primary bg-primary/10",
  paid: "border-emerald-500/40 text-emerald-700 bg-emerald-50",
  shipped: "border-primary/40 text-primary bg-primary/10",
  rejected: "border-destructive/40 text-destructive bg-destructive/10",
};

/** One row of the label-value table: the label fixed, the value free to breathe. */
const Fact = ({ label, children }) => (
  <div className="grid sm:grid-cols-[11rem_minmax(0,1fr)] gap-1 sm:gap-4 px-4 py-3">
    <dt className="overline sm:pt-0.5">{label}</dt>
    <dd className="min-w-0 break-words">{children}</dd>
  </div>
);

/**
 * One uploaded prescription: everything known about the request in a plain
 * label-value table, the file behind a fresh signed link, and below it the controls
 * that change something — status, notes and the Razorpay link.
 */
export const RxUploadDetail = ({ id, statuses, onClose, onSaved }) => {
  const [row, setRow] = useState(null);
  const [edit, setEdit] = useState({ admin_notes: "", payment_link: "" });
  const [pending, setPending] = useState("");
  const [draftAt, setDraftAt] = useState("");
  const [busy, setBusy] = useState(false);
  const queued = useRef({});
  const timer = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get(`/admin/prescription-uploads/${id}`);
      setRow(data);
      const draft = data.draft || {};
      // A draft is what the clinic last had on screen, so it wins over the saved record.
      setEdit({
        admin_notes: draft.admin_notes ?? data.admin_notes ?? "",
        payment_link: draft.payment_link ?? data.payment_link ?? "",
      });
      setPending(draft.status && draft.status !== data.status ? draft.status : "");
      setDraftAt(data.draft_saved_at || "");
      queued.current = {};
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      onClose();
    }
  };
  useEffect(() => { if (id) load(); }, [id]);

  // Everything half-done is parked on the server a few seconds after typing stops.
  // Nothing here reaches the patient — it is only so the work is not lost.
  const queueDraft = (patch) => {
    queued.current = { ...queued.current, ...patch };
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      try {
        const { data } = await api.put(`/admin/prescription-uploads/${id}/draft`, queued.current);
        setDraftAt(data.draft_saved_at);
      } catch {
        // A draft is never worth interrupting the clinic with an error.
      }
    }, 2500);
  };
  useEffect(() => () => clearTimeout(timer.current), []);

  const patch = async (body, message) => {
    clearTimeout(timer.current);
    setBusy(true);
    try {
      await api.patch(`/admin/prescription-uploads/${id}`, body);
      toast.success(message);
      setPending("");
      await load();
      onSaved?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  if (!row) return null;
  const isPdf = (row.file_content_type || "").includes("pdf");
  const fileUrl = mediaUrl(row.file_url);
  const waNumber = (row.phone || "").replace(/\D/g, "");
  const draft = row.draft || {};
  const link = row.rzp_link || null;

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="rx-upload-detail">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">{row.name}</DialogTitle>
          <DialogDescription>
            Review the prescription, call the patient, then build their order.
          </DialogDescription>
        </DialogHeader>

        <dl className="rounded-xl border border-border divide-y divide-border text-sm"
            data-testid="rx-detail-facts">
          <Fact label="Patient name">{row.name}</Fact>
          <Fact label="Mobile">
            <div className="flex flex-wrap items-center gap-2">
              <span>{row.phone}</span>
              <a href={`tel:${row.phone}`} data-testid="rx-detail-call">
                <Button variant="outline" size="sm" className="btn-pill h-7 text-xs">
                  <Phone size={11} className="mr-1" /> Call
                </Button>
              </a>
              <a href={`https://wa.me/${waNumber}`} target="_blank" rel="noreferrer" data-testid="rx-detail-whatsapp">
                <Button variant="outline" size="sm" className="btn-pill h-7 text-xs">
                  <MessageCircle size={11} className="mr-1" /> WhatsApp
                </Button>
              </a>
            </div>
          </Fact>
          <Fact label="Address">
            <span data-testid="rx-detail-address">{row.address || "— not given"}</span>
          </Fact>
          <Fact label="Received">{new Date(row.created_at).toLocaleString("en-IN")}</Fact>
          <Fact label="Source / doctor code">
            <div className="flex flex-wrap items-center gap-1.5">
              <Badge variant="outline" className="text-[10px]">{row.src}</Badge>
              {row.ref && (
                <Badge variant="outline" className="text-[10px] border-primary/30 text-primary bg-primary/5">
                  {row.ref}
                </Badge>
              )}
            </div>
          </Fact>
          <Fact label="Status">
            <div className="flex flex-wrap items-center gap-1.5">
              <Badge variant="outline" className={STATUS_TONE[row.status]} data-testid="rx-detail-status">
                {row.status}
              </Badge>
              {pending && (
                <Badge variant="outline" className="border-amber-500/50 text-amber-700 bg-amber-500/10 capitalize">
                  {pending} picked, not confirmed
                </Badge>
              )}
            </div>
          </Fact>
          <Fact label="Patient's note">
            <span data-testid="rx-detail-note">{row.note || "—"}</span>
          </Fact>
          <Fact label="Private notes">
            <div className="space-y-1">
              <span>{row.admin_notes || "—"}</span>
              {draft.admin_notes && draft.admin_notes !== row.admin_notes && (
                <p className="text-amber-700" data-testid="rx-detail-facts-draft-notes">
                  Draft: {draft.admin_notes}
                </p>
              )}
            </div>
          </Fact>
          <Fact label="Payment link">
            {link ? (
              <div className="space-y-1" data-testid="rx-detail-facts-paylink">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-medium">₹{Number(link.amount || 0).toFixed(2)}</span>
                  <Badge variant="outline" className="text-[10px] capitalize">{link.status}</Badge>
                  {link.amount_paid > 0 && (
                    <span className="text-xs text-muted-foreground">
                      ₹{Number(link.amount_paid).toFixed(2)} received
                    </span>
                  )}
                </div>
                <a href={link.short_url} target="_blank" rel="noreferrer"
                   className="text-primary underline break-all text-xs">
                  {link.short_url}
                </a>
              </div>
            ) : draft.pay_amount ? (
              <span className="text-amber-700" data-testid="rx-detail-facts-draft-amount">
                Draft: ₹{draft.pay_amount} — not sent yet
              </span>
            ) : (
              <span>— none sent</span>
            )}
          </Fact>
          <Fact label="Prescription file">
            <div className="flex flex-wrap items-start gap-3">
              <a href={fileUrl} target="_blank" rel="noreferrer" className="shrink-0">
                {isPdf ? (
                  <span className="w-16 h-20 rounded-lg border border-border bg-muted/30 flex items-center justify-center"
                        data-testid="rx-detail-thumb-pdf">
                    <FileText size={22} className="text-muted-foreground" />
                  </span>
                ) : (
                  <img src={fileUrl} alt="Prescription"
                       className="w-16 h-20 rounded-lg border border-border object-cover"
                       data-testid="rx-detail-thumb" />
                )}
              </a>
              <div className="space-y-1.5">
                <div className="flex flex-wrap gap-2">
                  <a href={fileUrl} target="_blank" rel="noreferrer" data-testid="rx-detail-open-file">
                    <Button variant="outline" size="sm" className="btn-pill h-7 text-xs">
                      <ExternalLink size={11} className="mr-1" /> Open
                    </Button>
                  </a>
                  <a href={fileUrl} download data-testid="rx-detail-download-file">
                    <Button variant="outline" size="sm" className="btn-pill h-7 text-xs">
                      <Download size={11} className="mr-1" /> Download
                    </Button>
                  </a>
                  <Button variant="ghost" size="sm" className="btn-pill h-7 text-xs" onClick={load}
                          data-testid="rx-detail-refresh-link">
                    <RefreshCw size={11} className="mr-1" /> Refresh link
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  The link works for 5 minutes and only for you.
                </p>
              </div>
            </div>
          </Fact>
        </dl>

        <div className="rounded-xl border border-border overflow-hidden bg-muted/20">
          {isPdf ? (
            <iframe src={fileUrl} title="Prescription" className="w-full h-[420px]" data-testid="rx-detail-pdf" />
          ) : (
            <img src={fileUrl} alt="Prescription" className="w-full max-h-[420px] object-contain" data-testid="rx-detail-image" />
          )}
        </div>

        <div className="pt-2">
          <h4 className="font-display text-xl mb-1">Update this request</h4>
          <p className="text-sm text-muted-foreground">
            Picking a status, typing notes or working out an amount changes nothing for the patient
            until you confirm or send.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="overline">Status</label>
            <div className="flex flex-wrap gap-2 mt-2">
              {statuses.map((s) => (
                <Button
                  key={s}
                  size="sm"
                  variant={(pending || row.status) === s ? "default" : "outline"}
                  disabled={busy}
                  className="btn-pill h-9 capitalize"
                  onClick={() => {
                    // Picked, not applied: confirm below once the call is finished.
                    const next = s === row.status ? "" : s;
                    setPending(next);
                    queueDraft({ status: next || row.status });
                  }}
                  data-testid={`rx-detail-set-${s}`}
                >
                  {s}
                </Button>
              ))}
            </div>
            {pending && (
              <div className="flex items-center gap-2 mt-3" data-testid="rx-detail-pending-status">
                <span className="text-xs text-muted-foreground">
                  Picked <span className="capitalize">{pending}</span> — not applied yet
                </span>
                <Button
                  size="sm"
                  disabled={busy}
                  className="btn-action h-8"
                  onClick={() => patch({ status: pending }, `Marked ${pending}`)}
                  data-testid="rx-detail-confirm-status"
                >
                  Confirm
                </Button>
                <Button size="sm" variant="ghost" className="btn-pill h-8"
                        onClick={() => { setPending(""); queueDraft({ status: row.status }); }}
                        data-testid="rx-detail-cancel-status">
                  Undo
                </Button>
              </div>
            )}
          </div>
          <div>
            <label className="overline">Razorpay payment link</label>
            <Input
              value={edit.payment_link}
              onChange={(e) => {
                setEdit((s) => ({ ...s, payment_link: e.target.value }));
                queueDraft({ payment_link: e.target.value });
              }}
              placeholder="https://rzp.io/i/…"
              className="mt-2 bg-transparent"
              data-testid="rx-detail-payment-link"
            />
          </div>
        </div>

        <div>
          <label className="overline">Private notes</label>
          <Textarea
            value={edit.admin_notes}
            onChange={(e) => {
              setEdit((s) => ({ ...s, admin_notes: e.target.value }));
              queueDraft({ admin_notes: e.target.value });
            }}
            rows={3}
            placeholder="What the patient said on the call, medicines agreed…"
            className="mt-2 bg-transparent"
            data-testid="rx-detail-notes"
          />
          {draftAt && (
            <p className="text-xs text-muted-foreground mt-2" data-testid="rx-detail-draft-note">
              Draft kept {new Date(draftAt).toLocaleTimeString("en-IN")} — nothing has been sent to
              the patient. It is here when you come back, and clears once you confirm.
            </p>
          )}
        </div>

        <RxPayLink
          upload={row}
          draft={row.draft}
          onDraft={queueDraft}
          onChanged={() => { load(); onSaved?.(); }}
        />

        <RxConvert upload={row} onConverted={() => { load(); onSaved?.(); }} />

        <div className="flex flex-wrap gap-2">
          <Button
            disabled={busy}
            className="btn-action h-11"
            onClick={() => patch(edit, "Saved")}
            data-testid="rx-detail-save"
          >
            Save
          </Button>
          <Button
            variant="outline"
            disabled={busy || row.status === "paid"}
            className="btn-pill h-11"
            onClick={() => patch({ ...edit, status: "paid" }, "Marked paid")}
            data-testid="rx-detail-mark-paid"
          >
            <FileText size={14} className="mr-1.5" /> Mark as paid
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export { STATUS_TONE };
export default RxUploadDetail;
